<script setup lang="ts">
import { Head, useForm } from '@inertiajs/vue3'
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import PublicInvoiceLayout from '@/Layouts/PublicInvoiceLayout.vue'
import Button from '@/Components/Utils/Button.vue'
import { useFormatTime } from '@/Composables/useFormatTime'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faCheckCircle, faEraser, faFileInvoice } from '@fal'
import { library } from '@fortawesome/fontawesome-svg-core'

library.add(faCheckCircle, faEraser, faFileInvoice)

defineOptions({ layout: PublicInvoiceLayout })

const props = defineProps<{
    title?: string
    data: {
        reference: string
        invoice_date: string | null
        invoice_url: string
        signed_at: string | null
        signature_image_url: string | null
        canceled_at: string | null
    }
    invoiceSetting: {
        company_name: string | null
        terms: string[]
    }
    storeUrl: string
}>()

const companyName = computed(() => props.invoiceSetting.company_name || 'Eatpack.ku')

const canvas = ref<HTMLCanvasElement | null>(null)
const hasDrawn = ref(false)
const isDrawing = ref(false)
const hasAgreed = ref(false)

const form = useForm({
    signature: '',
})

const context = (): CanvasRenderingContext2D | null => canvas.value?.getContext('2d') ?? null

const resizeCanvas = () => {
    const element = canvas.value
    if (!element) return

    const ratio = window.devicePixelRatio || 1
    const { width, height } = element.getBoundingClientRect()
    element.width = width * ratio
    element.height = height * ratio

    const ctx = context()
    if (!ctx) return

    ctx.scale(ratio, ratio)
    ctx.lineWidth = 2.5
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.strokeStyle = '#111827'
    hasDrawn.value = false
}

const pointerPosition = (event: PointerEvent) => {
    const rect = canvas.value!.getBoundingClientRect()
    return { x: event.clientX - rect.left, y: event.clientY - rect.top }
}

const startDrawing = (event: PointerEvent) => {
    const ctx = context()
    if (!ctx) return

    canvas.value!.setPointerCapture(event.pointerId)
    isDrawing.value = true
    const { x, y } = pointerPosition(event)
    ctx.beginPath()
    ctx.moveTo(x, y)
    ctx.lineTo(x, y)
    ctx.stroke()
    hasDrawn.value = true
    form.clearErrors('signature')
}

const draw = (event: PointerEvent) => {
    if (!isDrawing.value) return

    const ctx = context()
    if (!ctx) return

    const { x, y } = pointerPosition(event)
    ctx.lineTo(x, y)
    ctx.stroke()
}

const stopDrawing = () => {
    isDrawing.value = false
}

const clearCanvas = () => {
    const element = canvas.value
    const ctx = context()
    if (!element || !ctx) return

    ctx.save()
    ctx.setTransform(1, 0, 0, 1, 0, 0)
    ctx.clearRect(0, 0, element.width, element.height)
    ctx.restore()
    hasDrawn.value = false
}

const submit = () => {
    if (!canvas.value || !hasDrawn.value || !hasAgreed.value) return

    form.signature = canvas.value.toDataURL('image/png')
    form.post(props.storeUrl, { preserveScroll: true })
}

onMounted(() => {
    resizeCanvas()
    window.addEventListener('resize', resizeCanvas)
})

onBeforeUnmount(() => window.removeEventListener('resize', resizeCanvas))
</script>

<template>
    <Head :title="title" />

    <div class="overflow-hidden rounded-2xl bg-white shadow-sm">
        <div class="flex flex-wrap items-center justify-between gap-4 border-b border-gray-100 p-6">
            <div class="flex items-center gap-x-4">
                <img src="/logo-eatpackku.webp" :alt="companyName" class="h-14 w-14 shrink-0 rounded-full" />
                <div>
                    <div class="text-lg font-bold text-gray-800">Konfirmasi Pesanan</div>
                    <div class="font-mono text-sm text-gray-500">{{ data.reference }}</div>
                </div>
            </div>
            <a :href="data.invoice_url" class="inline-flex items-center gap-x-2 text-sm text-blue-500 underline">
                <FontAwesomeIcon icon="fal fa-file-invoice" fixed-width aria-hidden="true" />
                Lihat invoice
            </a>
        </div>

        <dl class="border-b border-gray-100 p-6 text-sm">
            <dt class="text-xs uppercase tracking-wide text-gray-400">Tanggal pesanan</dt>
            <dd>{{ useFormatTime(data.invoice_date) }}</dd>
        </dl>

        <div class="border-b border-gray-100 bg-gray-50 p-6 text-sm">
            <p class="mb-3 text-gray-700">
                Dengan menandatangani halaman ini, Anda menyatakan telah membaca, memahami, dan
                <span class="font-semibold">menyetujui seluruh syarat & ketentuan yang berlaku di Eatpack.ku</span>
                untuk pesanan <span class="font-mono font-semibold">{{ data.reference }}</span>.
            </p>
            <template v-if="invoiceSetting.terms.length">
                <div class="mb-2 text-xs uppercase tracking-wide text-gray-400">Syarat & ketentuan</div>
                <ol class="flex list-decimal flex-col gap-1 pl-4 text-gray-600">
                    <li v-for="(term, index) in invoiceSetting.terms" :key="`term-${index}`">{{ term }}</li>
                </ol>
            </template>
        </div>

        <div v-if="data.signed_at" class="flex flex-col items-center gap-3 p-6 text-center">
            <FontAwesomeIcon icon="fal fa-check-circle" class="text-4xl text-emerald-500" fixed-width aria-hidden="true" />
            <div class="font-semibold text-gray-800">Pesanan sudah ditandatangani</div>
            <div class="text-sm text-gray-500">{{ useFormatTime(data.signed_at, { formatTime: 'hms' }) }}</div>
            <img
                v-if="data.signature_image_url"
                :src="data.signature_image_url"
                alt="Tanda tangan"
                class="max-h-40 w-full max-w-md rounded-lg border border-gray-200 object-contain"
            />
        </div>

        <div v-else-if="data.canceled_at" class="p-6 text-center text-sm text-gray-500">
            Pesanan ini sudah dibatalkan, tidak perlu ditandatangani.
        </div>

        <div v-else class="flex flex-col gap-4 p-6">
            <div>
                <div class="mb-2 flex items-center justify-between">
                    <div class="text-xs uppercase tracking-wide text-gray-400">Tanda tangan di bawah ini</div>
                    <button
                        type="button"
                        class="inline-flex items-center gap-x-1 text-xs text-gray-500 hover:text-gray-700 disabled:opacity-40"
                        :disabled="!hasDrawn"
                        @click="clearCanvas"
                    >
                        <FontAwesomeIcon icon="fal fa-eraser" fixed-width aria-hidden="true" />
                        Ulangi
                    </button>
                </div>
                <canvas
                    ref="canvas"
                    class="h-56 w-full touch-none rounded-lg border-2 border-dashed bg-white"
                    :class="form.errors.signature ? 'border-red-400' : 'border-gray-300'"
                    @pointerdown.prevent="startDrawing"
                    @pointermove.prevent="draw"
                    @pointerup="stopDrawing"
                    @pointercancel="stopDrawing"
                    @pointerleave="stopDrawing"
                />
                <div v-if="form.errors.signature" class="mt-1 text-xs text-red-500">{{ form.errors.signature }}</div>
            </div>

            <label class="flex items-start gap-x-2 text-sm text-gray-700">
                <input v-model="hasAgreed" type="checkbox" class="mt-0.5 rounded border-gray-300" />
                Saya menyetujui syarat & ketentuan Eatpack.ku untuk pesanan ini.
            </label>

            <div class="mt-4 w-full">
                <Button
                    full
                    type="save"
                    label="Kirim tanda tangan"
                    :disabled="!hasDrawn || !hasAgreed"
                    :loading="form.processing"
                    @click="submit"
                />
            </div>
        </div>
    </div>
</template>
